import { useState } from 'react';
import { useApi } from '../hooks/useApi.js';

interface CasbEvent {
  id: string;
  platform: string;
  event_type: string;
  severity: string;
  description: string;
  detected_at: string;
  status: string;
}

interface UserRiskResponse {
  data: {
    user_email: string;
    risk_score: number;
    event_count: number;
    platforms: string[];
    events_by_severity: Record<string, number>;
    recent_events: CasbEvent[];
  };
}

interface SummaryResponse {
  data: {
    top_risky_users: Array<{
      email: string;
      risk_score: number;
      event_count: number;
      platforms: string[];
    }>;
  };
}

const PLATFORM_ICONS: Record<string, string> = {
  google_workspace: '🔵',
  slack: '💬',
  dropbox: '📦',
  salesforce: '☁️',
};

const EVENT_TYPE_LABELS: Record<string, string> = {
  suspicious_signin: 'Suspicious Sign-in',
  failed_login_spike: 'Failed Login Spike',
  mfa_disabled: 'MFA Disabled',
  admin_role_granted: 'Admin Role Granted',
  password_changed: 'Password Changed',
  session_from_new_location: 'New Location Login',
  bulk_download: 'Bulk Download',
  bulk_delete: 'Bulk Delete',
  external_share: 'External Share',
  public_share: 'Public Share',
  oauth_app_authorized: 'OAuth App Authorized',
  oauth_app_scopes_elevated: 'Scopes Elevated',
  third_party_app_installed: 'App Installed',
  security_setting_changed: 'Security Setting Changed',
};

export function CasbUserRiskPage() {
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const { data: summaryData, loading, error, refresh } = useApi<SummaryResponse>('/protect/casb/summary');
  const { data: userRiskData, loading: userLoading } = useApi<UserRiskResponse>(
    selectedUser ? `/protect/casb/users/${encodeURIComponent(selectedUser)}/risk` : null,
  );

  const topUsers = summaryData?.data?.top_risky_users ?? [];
  const userRisk = userRiskData?.data;

  function severityBadge(severity: string) {
    const colors =
      severity === 'critical' ? 'bg-red-100 text-red-700' :
      severity === 'high' ? 'bg-orange-100 text-orange-700' :
      severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
      'bg-emerald-100 text-emerald-700';
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors} capitalize`}>{severity}</span>;
  }

  function riskLevel(score: number): { label: string; color: string } {
    if (score >= 15) return { label: 'Critical', color: 'text-red-600' };
    if (score >= 10) return { label: 'High', color: 'text-orange-600' };
    if (score >= 5) return { label: 'Medium', color: 'text-yellow-600' };
    return { label: 'Low', color: 'text-emerald-600' };
  }

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-gray-500">Loading user risk data...</div></div>;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
        <button onClick={refresh} className="mt-2 text-sm text-red-600 underline">Retry</button>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">User Risk</h2>
      <p className="text-gray-600 mb-6">Cross-platform user risk analysis based on CASB events.</p>

      <div className="flex gap-6">
        {/* User List */}
        <div className="w-1/2">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Risk Leaderboard</h3>
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">User</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Risk</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Events</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Platforms</th>
                </tr>
              </thead>
              <tbody>
                {topUsers.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500">No user risk data yet.</td></tr>
                ) : topUsers.map((u) => {
                  const risk = riskLevel(u.risk_score);
                  return (
                    <tr
                      key={u.email}
                      className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${selectedUser === u.email ? 'bg-emerald-50' : ''}`}
                      onClick={() => setSelectedUser(u.email)}
                    >
                      <td className="px-4 py-3 font-medium text-gray-900">{u.email}</td>
                      <td className="px-4 py-3">
                        <span className={`font-bold ${risk.color}`}>{u.risk_score}</span>
                        <span className="text-xs text-gray-400 ml-1">{risk.label}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{u.event_count}</td>
                      <td className="px-4 py-3">{u.platforms.map(p => PLATFORM_ICONS[p] ?? p).join(' ')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* User Detail */}
        <div className="w-1/2">
          {selectedUser && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">User Profile: {selectedUser}</h3>
              {userLoading ? (
                <div className="text-gray-500">Loading...</div>
              ) : userRisk ? (
                <div className="space-y-4">
                  {/* Risk Summary Cards */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-white rounded-lg border border-gray-200 p-3">
                      <p className="text-xs text-gray-500">Risk Score</p>
                      <p className={`text-xl font-bold ${riskLevel(userRisk.risk_score).color}`}>{userRisk.risk_score}</p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-3">
                      <p className="text-xs text-gray-500">Events</p>
                      <p className="text-xl font-bold text-gray-900">{userRisk.event_count}</p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-3">
                      <p className="text-xs text-gray-500">Platforms</p>
                      <p className="text-xl">{userRisk.platforms.map(p => PLATFORM_ICONS[p] ?? p).join(' ')}</p>
                    </div>
                  </div>

                  {/* Severity Breakdown */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Severity Breakdown</p>
                    <div className="flex gap-3">
                      {Object.entries(userRisk.events_by_severity).map(([sev, count]) => (
                        count > 0 && (
                          <div key={sev} className="flex items-center gap-1">
                            {severityBadge(sev)}
                            <span className="text-sm font-medium">{count}</span>
                          </div>
                        )
                      ))}
                    </div>
                  </div>

                  {/* Recent Events */}
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                      <p className="text-sm font-medium text-gray-700">Recent Events</p>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {userRisk.recent_events.map((e) => (
                        <div key={e.id} className="px-4 py-3">
                          <div className="flex items-center gap-2 mb-1">
                            <span>{PLATFORM_ICONS[e.platform] ?? e.platform}</span>
                            {severityBadge(e.severity)}
                            <span className="text-xs text-gray-400">{new Date(e.detected_at).toLocaleString()}</span>
                          </div>
                          <p className="text-sm text-gray-900">{EVENT_TYPE_LABELS[e.event_type] ?? e.event_type}</p>
                          <p className="text-xs text-gray-500">{e.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-gray-500">Select a user to view details.</div>
              )}
            </div>
          )}

          {!selectedUser && (
            <div className="flex items-center justify-center h-64 text-gray-400">
              Select a user from the leaderboard to view their risk profile.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
